// src/store/wallet/index.js
import axios from '@/libs/axios'

let currencyRatesRequest = null
let currencyRatesRefreshTimer = null

export const WALLET_CURRENCY_RATES_CACHE_TTL_MS = 5 * 60 * 1000
export const WALLET_CURRENCY_RATES_REFRESH_INTERVAL_MS = 10 * 60 * 1000
export const WALLET_CURRENCY_RATES_INCLUDE = 'from_currency,to_currency'
export const WALLET_DYNAMIC_TOPUP_BUY_ENDPOINT = '/currencies/buy'

const normalizeCurrencyCode = value => {
  if (!value) return ''

  if (typeof value === 'object') {
    return String(value.code || value.currency_code || value.iso_code || '')
      .trim()
      .toUpperCase()
  }

  return String(value)
    .trim()
    .toUpperCase()
}

const extractCollection = response => {
  const payload = response && response.data ? response.data : response

  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload && payload.data)) return payload.data
  if (payload && payload.data && typeof payload.data === 'object') return [payload.data]
  if (payload && typeof payload === 'object') return [payload]

  return []
}

const normalizePaymentCheckoutResponse = response => {
  const responsePayload = response?.data ?? null

  // LiqPay direct response: { url: '...', data: '...', signature: '...' }.
  // In this case `data` is LiqPay encoded form data, not an API wrapper.
  if (
    responsePayload
    && typeof responsePayload === 'object'
    && !Array.isArray(responsePayload)
    && (
      responsePayload.url
      || responsePayload.checkout_url
      || responsePayload.redirect_url
      || responsePayload.signature
    )
  ) {
    return responsePayload
  }

  // Standard API wrapper: { data: { ... } }.
  if (
    responsePayload
    && responsePayload.data
    && typeof responsePayload.data === 'object'
    && !Array.isArray(responsePayload.data)
  ) {
    return responsePayload.data
  }

  return responsePayload
}

const getCurrencyRateCacheKey = (fromCurrency, toCurrency) => ([
  normalizeCurrencyCode(fromCurrency),
  normalizeCurrencyCode(toCurrency),
].join(':'))

const normalizeCurrencyRate = currencyRate => {
  if (!currencyRate || typeof currencyRate !== 'object') return null

  const fromCurrency = currencyRate.from_currency || currencyRate.fromCurrency || null
  const toCurrency = currencyRate.to_currency || currencyRate.toCurrency || null
  const fromCurrencyCode = normalizeCurrencyCode(
    currencyRate.fromCurrencyCode
    || currencyRate.from_currency_code
    || fromCurrency,
  )
  const toCurrencyCode = normalizeCurrencyCode(
    currencyRate.toCurrencyCode
    || currencyRate.to_currency_code
    || toCurrency,
  )
  const rate = Number(currencyRate.rate)

  if (!fromCurrencyCode || !toCurrencyCode || !Number.isFinite(rate) || rate <= 0) return null

  return {
    ...currencyRate,
    rate,
    fromCurrency,
    toCurrency,
    fromCurrencyCode,
    toCurrencyCode,
  }
}

const normalizeCurrencyRates = collection => (
  Array.isArray(collection)
    ? collection
      .map(normalizeCurrencyRate)
      .filter(Boolean)
    : []
)

const getRateFromCollectionByPair = (collection, fromCurrency, toCurrency) => {
  const key = getCurrencyRateCacheKey(fromCurrency, toCurrency)

  return normalizeCurrencyRates(collection).find(rate => (
    getCurrencyRateCacheKey(rate.fromCurrencyCode, rate.toCurrencyCode) === key
  )) || null
}

const state = () => ({
  wallet: null,
  transactions: [],
  transactionsLoading: false,
  transactionsError: null,
  transactionsLastFetchedAt: null,

  // packages
  packages: [],
  packagesLoading: false,
  packagesError: null,
  packagesLastFetchedAt: null,
  packageBuyLoading: false,
  packageBuyError: null,

  // selected transaction status
  transaction: null,
  transactionLoading: false,
  transactionError: null,
  transactionLastFetchedAt: null,

  loading: false,
  error: null,
  lastFetchedAt: null,

  // promo codes
  promoLoading: false,
  promoError: null,

  // currency rates
  currencyRates: [],
  currencyRatesLoading: false,
  currencyRatesError: null,
  currencyRatesFetchedAt: null,
  currencyRatesAutoRefreshStarted: false,
})

const getters = {
  // wallet
  wallet: s => s.wallet,
  currency: s => s.wallet?.currency || null,
  totalBalance: s => s.wallet?.total_balance ?? null,
  frozenBalance: s => s.wallet?.frozen_balance ?? null,
  loading: s => s.loading,
  error: s => s.error,
  lastFetchedAt: s => s.lastFetchedAt,

  // transactions
  transactions: s => s.transactions,
  transactionsLoading: s => s.transactionsLoading,
  transactionsError: s => s.transactionsError,
  transactionsLastFetchedAt: s => s.transactionsLastFetchedAt,

  // packages
  packages: s => s.packages,
  packagesLoading: s => s.packagesLoading,
  packagesError: s => s.packagesError,
  packagesLastFetchedAt: s => s.packagesLastFetchedAt,
  packageBuyLoading: s => s.packageBuyLoading,
  packageBuyError: s => s.packageBuyError,

  // selected transaction status
  transaction: s => s.transaction,
  transactionLoading: s => s.transactionLoading,
  transactionError: s => s.transactionError,
  transactionLastFetchedAt: s => s.transactionLastFetchedAt,

  // promo codes
  promoLoading: s => s.promoLoading,
  promoError: s => s.promoError,

  // currency rates
  currencyRates: s => s.currencyRates,
  currencyRatesLoading: s => s.currencyRatesLoading,
  currencyRatesError: s => s.currencyRatesError,
  currencyRatesFetchedAt: s => s.currencyRatesFetchedAt,
  currencyRatesAutoRefreshStarted: s => s.currencyRatesAutoRefreshStarted,
  currencyRateByPair: s => (fromCurrency, toCurrency) => getRateFromCollectionByPair(
    s.currencyRates,
    fromCurrency,
    toCurrency,
  ),
}

const mutations = {
  // wallet
  SET_LOADING(state, val) {
    state.loading = Boolean(val)
  },
  SET_ERROR(state, err) {
    state.error = err || null
  },
  SET_WALLET(state, wallet) {
    state.wallet = wallet || null
  },
  SET_LAST_FETCHED_AT(state, ts) {
    state.lastFetchedAt = ts || null
  },

  // transactions
  SET_TX_LOADING(state, val) {
    state.transactionsLoading = Boolean(val)
  },
  SET_TX_ERROR(state, err) {
    state.transactionsError = err || null
  },
  SET_TRANSACTIONS(state, items) {
    state.transactions = Array.isArray(items) ? items : []
  },
  SET_TX_LAST_FETCHED_AT(state, ts) {
    state.transactionsLastFetchedAt = ts || null
  },

  // packages
  SET_PACKAGES_LOADING(state, val) {
    state.packagesLoading = Boolean(val)
  },
  SET_PACKAGES_ERROR(state, err) {
    state.packagesError = err || null
  },
  SET_PACKAGES(state, items) {
    state.packages = Array.isArray(items) ? items : []
  },
  SET_PACKAGES_LAST_FETCHED_AT(state, ts) {
    state.packagesLastFetchedAt = ts || null
  },
  SET_PACKAGE_BUY_LOADING(state, val) {
    state.packageBuyLoading = Boolean(val)
  },
  SET_PACKAGE_BUY_ERROR(state, err) {
    state.packageBuyError = err || null
  },

  // selected transaction status
  SET_TRANSACTION(state, transaction) {
    state.transaction = transaction || null
  },
  SET_TRANSACTION_LOADING(state, val) {
    state.transactionLoading = Boolean(val)
  },
  SET_TRANSACTION_ERROR(state, err) {
    state.transactionError = err || null
  },
  SET_TRANSACTION_LAST_FETCHED_AT(state, ts) {
    state.transactionLastFetchedAt = ts || null
  },

  // promo codes
  SET_PROMO_LOADING(state, val) { state.promoLoading = Boolean(val) },
  SET_PROMO_ERROR(state, err) { state.promoError = err || null },

  // currency rates
  SET_CURRENCY_RATES(state, payload) {
    const source = payload && Object.prototype.hasOwnProperty.call(payload, 'rates')
      ? payload.rates
      : payload

    state.currencyRates = normalizeCurrencyRates(source)
    state.currencyRatesFetchedAt = new Date().toISOString()
  },
  SET_CURRENCY_RATES_LOADING(state, val) {
    state.currencyRatesLoading = Boolean(val)
  },
  SET_CURRENCY_RATES_ERROR(state, err) {
    state.currencyRatesError = err || null
  },
  SET_CURRENCY_RATES_AUTO_REFRESH_STARTED(state, val) {
    state.currencyRatesAutoRefreshStarted = Boolean(val)
  },
  RESET_CURRENCY_RATES_CACHE(state) {
    state.currencyRates = []
    state.currencyRatesFetchedAt = null
  },

  RESET(state) {
    state.wallet = null
    state.transactions = []

    state.loading = false
    state.error = null
    state.lastFetchedAt = null

    state.transactionsLoading = false
    state.transactionsError = null
    state.transactionsLastFetchedAt = null

    // packages
    state.packages = []
    state.packagesLoading = false
    state.packagesError = null
    state.packagesLastFetchedAt = null
    state.packageBuyLoading = false
    state.packageBuyError = null

    // selected transaction status
    state.transaction = null
    state.transactionLoading = false
    state.transactionError = null
    state.transactionLastFetchedAt = null

    // currency rates
    state.currencyRates = []
    state.currencyRatesLoading = false
    state.currencyRatesError = null
    state.currencyRatesFetchedAt = null
    state.currencyRatesAutoRefreshStarted = false
  },
}

const actions = {
  reset({ commit }) {
    if (currencyRatesRefreshTimer) {
      clearInterval(currencyRatesRefreshTimer)
      currencyRatesRefreshTimer = null
    }

    commit('RESET')
  },

  // GET /wallet -> { data: { ...wallet } }
  async fetchWallet({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const res = await axios.get('/wallet', { params })

      const wallet = res?.data?.data ?? null

      const normalizeDecimal = v => (typeof v === 'string' && v.toLowerCase().includes('unknown type') ? null : v)

      const normalized = wallet
        ? {
          ...wallet,
          total_balance: normalizeDecimal(wallet.total_balance),
          frozen_balance: normalizeDecimal(wallet.frozen_balance),
        }
        : null

      commit('SET_WALLET', normalized)
      commit('SET_LAST_FETCHED_AT', new Date().toISOString())

      return normalized
    } catch (err) {
      commit('SET_ERROR', err)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // GET /transactions -> { data: [ ... ] }
  async fetchTransactions({ commit }, params = {}) {
    commit('SET_TX_LOADING', true)
    commit('SET_TX_ERROR', null)

    try {
      const res = await axios.get('/transactions', { params })

      const items = res?.data?.data ?? []

      const normalizeField = v => (typeof v === 'string' && v.toLowerCase().includes('unknown type') ? null : v)

      const normalized = Array.isArray(items)
        ? items.map(tx => ({
          ...tx,
          amount: normalizeField(tx.amount),
          description: normalizeField(tx.description),
        }))
        : []

      commit('SET_TRANSACTIONS', normalized)
      commit('SET_TX_LAST_FETCHED_AT', new Date().toISOString())

      return res
    } catch (err) {
      commit('SET_TX_ERROR', err)
      throw err
    } finally {
      commit('SET_TX_LOADING', false)
    }
  },

  // GET /public/currencies/packages -> { data: [ ... ] }
  async fetchPackages({ commit }, params = {}) {
    commit('SET_PACKAGES_LOADING', true)
    commit('SET_PACKAGES_ERROR', null)

    try {
      const res = await axios.get('/public/currencies/packages', { params })
      const items = res?.data?.data ?? []

      const toNumOrNull = v => {
        const n = Number(v)
        return Number.isFinite(n) ? n : null
      }

      const normalized = Array.isArray(items)
        ? items.map(p => ({
          ...p,
          amount: toNumOrNull(p.amount),
          price: toNumOrNull(p.price),
        }))
        : []

      commit('SET_PACKAGES', normalized)
      commit('SET_PACKAGES_LAST_FETCHED_AT', new Date().toISOString())

      return normalized
    } catch (err) {
      commit('SET_PACKAGES_ERROR', err)
      throw err
    } finally {
      commit('SET_PACKAGES_LOADING', false)
    }
  },

  // POST /currencies/packages/{id}/buy
  async buyPackage({ commit }, { id, ...payload } = {}) {
    commit('SET_PACKAGE_BUY_LOADING', true)
    commit('SET_PACKAGE_BUY_ERROR', null)

    try {
      const res = await axios.post(`/currencies/packages/${id}/buy`, payload)

      return normalizePaymentCheckoutResponse(res)
    } catch (err) {
      commit('SET_PACKAGE_BUY_ERROR', err)
      throw err
    } finally {
      commit('SET_PACKAGE_BUY_LOADING', false)
    }
  },

  // POST /currencies/buy
  async buyTopupAmount({ commit }, payload = {}) {
    commit('SET_PACKAGE_BUY_LOADING', true)
    commit('SET_PACKAGE_BUY_ERROR', null)

    try {
      const res = await axios.post(WALLET_DYNAMIC_TOPUP_BUY_ENDPOINT, payload)

      return normalizePaymentCheckoutResponse(res)
    } catch (err) {
      commit('SET_PACKAGE_BUY_ERROR', err)
      throw err
    } finally {
      commit('SET_PACKAGE_BUY_LOADING', false)
    }
  },

  // GET /transactions/{id} -> { data: { ...transaction } }
  async fetchTransaction({ commit }, payload = {}) {
    const {
      transaction_id: transactionIdFromSnake,
      transactionId: transactionIdFromCamel,
      order_id: legacyOrderIdFromSnake,
      orderId: legacyOrderIdFromCamel,
      id,
      ...params
    } = payload || {}

    const transactionId = transactionIdFromSnake
      || transactionIdFromCamel
      || legacyOrderIdFromSnake
      || legacyOrderIdFromCamel
      || id

    if (!transactionId) {
      const err = new Error('Transaction id is required')
      commit('SET_TRANSACTION_ERROR', err)
      throw err
    }

    commit('SET_TRANSACTION_LOADING', true)
    commit('SET_TRANSACTION_ERROR', null)

    try {
      const res = await axios.get(
        `/transactions/${encodeURIComponent(transactionId)}`,
        { params },
      )

      const transaction = res?.data?.data ?? res?.data ?? null

      commit('SET_TRANSACTION', transaction)
      commit('SET_TRANSACTION_LAST_FETCHED_AT', new Date().toISOString())

      return transaction
    } catch (err) {
      commit('SET_TRANSACTION_ERROR', err)
      throw err
    } finally {
      commit('SET_TRANSACTION_LOADING', false)
    }
  },

  // GET /public/currency-rates?include=from_currency,to_currency -> { data: [ ... ] }
  async fetchCurrencyRates({ commit, state }, payload = {}) {
    const maxAgeMs = Number(payload.maxAgeMs || WALLET_CURRENCY_RATES_CACHE_TTL_MS)
    const fetchedAt = state.currencyRatesFetchedAt ? Date.parse(state.currencyRatesFetchedAt) : 0
    const hasFreshRates = Boolean(
      state.currencyRates.length
      && fetchedAt
      && Date.now() - fetchedAt < maxAgeMs,
    )

    if (!payload.force && hasFreshRates) {
      return state.currencyRates
    }

    if (currencyRatesRequest) return currencyRatesRequest

    commit('SET_CURRENCY_RATES_LOADING', true)
    commit('SET_CURRENCY_RATES_ERROR', null)

    currencyRatesRequest = axios.get('/public/currency-rates', {
      params: {
        include: WALLET_CURRENCY_RATES_INCLUDE,
      },
    })

    try {
      const res = await currencyRatesRequest
      const rates = extractCollection(res)

      commit('SET_CURRENCY_RATES', rates)

      return normalizeCurrencyRates(rates)
    } catch (err) {
      commit('SET_CURRENCY_RATES_ERROR', err)
      throw err
    } finally {
      currencyRatesRequest = null
      commit('SET_CURRENCY_RATES_LOADING', false)
    }
  },

  startCurrencyRatesAutoRefresh({ commit, dispatch }, payload = {}) {
    if (!currencyRatesRefreshTimer) {
      const intervalMs = Number(payload.intervalMs || WALLET_CURRENCY_RATES_REFRESH_INTERVAL_MS)

      currencyRatesRefreshTimer = setInterval(() => {
        dispatch('fetchCurrencyRates', {
          ...payload,
          force: true,
        }).catch(() => {})
      }, intervalMs)

      commit('SET_CURRENCY_RATES_AUTO_REFRESH_STARTED', true)
    }

    return dispatch('fetchCurrencyRates', payload)
  },

  stopCurrencyRatesAutoRefresh({ commit }) {
    if (currencyRatesRefreshTimer) {
      clearInterval(currencyRatesRefreshTimer)
      currencyRatesRefreshTimer = null
    }

    commit('SET_CURRENCY_RATES_AUTO_REFRESH_STARTED', false)
  },

  // POST /promo-codes/deposit -> { data: { code } } (example)
  async redeemPromocode({ commit }, { code } = {}) {
    commit('SET_PROMO_LOADING', true)
    commit('SET_PROMO_ERROR', null)

    try {
      const res = await axios.post('/promo-codes/deposit', { code })

      // normalize
      const data = res?.data?.data ?? res?.data ?? null

      // optionally refresh wallet state if API returns wallet
      if (data?.wallet) {
        commit('SET_WALLET', data.wallet)
      }

      return data
    } catch (err) {
      commit('SET_PROMO_ERROR', err)
      throw err
    } finally {
      commit('SET_PROMO_LOADING', false)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
